<template>
<div id="policycaseBatchManagement">
  <div class="container" id="policycaseBatchManagement-container">
    <div class="table-responsive mt-1" :class="{ 'table-inactive': batchSelectionDisabled }" id="policycaseBatchManagement-tableDiv">
      <table class="table table-striped table-hover" id="policycaseBatchManagement-table">
        <caption class="accessibly-hidden">{{ 'POLICYCASE_BATCH_MANAGEMENT' | translate }}</caption>
        <template v-for="(group, batchIdKey) in groupedBatches">
          <tr class="d-lg-flex" :id="'policycaseBatchManagement-tableRow-'+batchIdKey">
            <th scope="col" class="col-lg-3" :id="'policycaseBatchManagement-tableCol-'+batchIdKey">
              <div class="custom-control alternative custom-checkbox" :id="'policycaseBatchManagement-tableCheckbox-'+batchIdKey">
                <input class="custom-control-input" type="checkbox" :id="'checkbox_'+batchIdKey"
                       :value="batchIdKey" v-model="selectedFullBatchIds" @change="toggleSelectBatch($event, batchIdKey)">
                <label class="custom-control-label" :for="'checkbox_'+batchIdKey" :id="'policycaseBatchManagement-tableCheckbox-label-'+batchIdKey">
                  {{ batchIdKey }}
                </label>
              </div>
            </th>
            <th scope="col" class="col-lg-2">{{ 'PRODUCT_LINE' | translate }}</th>
            <th scope="col" class="col-lg-2">{{ 'STATUS' | translate }}</th>
            <th scope="col" class="col-lg-2">{{ 'COUNT' | translate }}</th>
            <th scope="col" class="col-lg-3"></th>
          </tr>
          <tbody :id="'policycaseBatchManagement-tableBody-'+batchIdKey">
            <template v-for="(productLineName, productLineKey) in group">
              <tr v-for="(status, statusKey) in productLineName" class="d-lg-flex" :id="'policycaseBatchManagement-tableBody-'+batchIdKey+'-'+productLineKey+'-'+statusKey">
                <td class="col-lg-3" :id="'policycaseBatchManagement-tableBody-data-'+batchIdKey+'-'+productLineKey+'-'+statusKey">
                  <div class="custom-control alternative custom-checkbox ml-4" :id="'policycaseBatchManagement-tableBody-checkbox-'+batchIdKey+'-'+productLineKey+'-'+statusKey">
                    <input class="custom-control-input" type="checkbox"
                           :id="'checkbox_'+batchIdKey+'_'+productLineKey+'_'+statusKey" :value="status" v-model="selected">
                    <label class="custom-control-label" :for="'checkbox_'+batchIdKey+'_'+productLineKey+'_'+statusKey" :id="'checkbox-label-'+batchIdKey+'_'+productLineKey+'_'+statusKey"></label>
                  </div>
                </td>
                <td class="col-lg-2 font-weight-semibold">{{ getDescriptionByName(productLineKey) }}</td>
                <td class="col-lg-2">{{ statusKey | translate('status') }}</td>
                <td class="col-lg-2">{{ status.count }}</td>
                <td class="col-lg-3">
                  <small v-if="underRecalculation(batchIdKey, productLineKey, statusKey).inQueue">
                    <span v-if="underRecalculation(batchIdKey, productLineKey, statusKey).status === 'RUNNING'" class="text-success">
                      <i class="fa fa-refresh fa-spin mr-1" aria-hidden="true"></i> {{ 'RECALCULATING' | translate }}
                    </span>
                    <span v-else class="text-info">{{ 'PENDING_RECALCULATION' | translate }}</span>
                    <router-link :to="'/job/' + underRecalculation(batchIdKey, productLineKey, statusKey).jobId" class="ml-3">
                      {{ 'GO_TO_JOB' | translate }} <i class="fa fa-arrow-right ml-1" aria-hidden="true"></i>
                    </router-link>
                  </small>
                  <small v-else-if="underClosing(batchIdKey, productLineKey, statusKey).inQueue">
                    <span v-if="underClosing(batchIdKey, productLineKey, statusKey).status === 'RUNNING'" class="text-success">
                      <i class="fa fa-refresh fa-spin mr-1" aria-hidden="true"></i> {{ 'CLOSING' | translate }}
                    </span>
                    <span v-else class="text-info">{{ 'PENDING_CLOSE' | translate }}</span>
                  </small>
                  <small v-else-if="underDeletion(batchIdKey, productLineKey, statusKey).inQueue">
                    <span v-if="underDeletion(batchIdKey, productLineKey, statusKey).status === 'RUNNING'" class="text-danger">
                      <i class="fa fa-refresh fa-spin mr-1" aria-hidden="true"></i> {{ 'DELETING' | translate }}
                    </span>
                    <span v-else class="text-info">{{ 'PENDING_DELETE' | translate }}</span>
                  </small>
                </td>
              </tr>
            </template>
          </tbody>
        </template>
      </table>
    </div>
    <div v-if="filteredBatches.length === 0" class="text-center mt-4" id="policycaseBatchManagement-noBatchFoundDiv">{{ 'NO_BATCHES_FOUND' | translate }}</div>
  </div>
</div>
</template>

<script>
import RestService from '../../service/RestService.js';
import { Roles } from '../../service/keycloak/Roles';
import GeneralConfigService from '../../service/GeneralConfigService';

export default {
  components: {},
  props: [ 'jobs', 'batchFilter' ],
  data: function() {
    return {
      rest: this.initRestService(),
      roles: Roles,
      batches: [], // { batchId: string, productLine: string, status: string, count: number }
      // compositeId is the concatenation of batchId + productLine + status of the batch ,
      // ex. BASISPRODTESTHOUSECOMBIACCEPTED
      recalculatingBatchesCompositeIds: [],
      closingBatchesCompositeIds: [],
      deletingBatchesCompositeIds: [],
      selected: [], // { batchId: string, productLine: string, status: string, count: number }
      selectedFullBatchIds: [],
      interval: null,
      rejectedStatusesRecalc: ['CLOSED', 'HISTORICAL'],
      rejectedStatusesClose: ['CLOSED', 'HISTORICAL', 'REOPENED'],
      batchSelectionDisabled: false,
      productLines: []
    }
  },
  beforeMount() {
    this.init();
  },
  async created() {
    await this.getProductlines();
  },
  mounted() {
    this.$emit('set-active-component', 'POLICYCASE_BATCH_MANAGEMENT');
  },
  beforeRouteLeave(to, from, next) {
    clearInterval(this.interval);
    next();
  },
  computed: {
    actionsAndAllowedRoles() {
      return {
        recalc: [ this.roles.ADMIN, this.roles.BATCH_RECALC ],
        close: [ this.roles.ADMIN, this.roles.BATCH_CLOSE ],
        delete: [ this.roles.ADMIN, this.roles.BATCH_DELETE ]
      }
    },
    filteredBatches() {
      if (this.batchFilter == null || this.batchFilter.length < 1) return this.batches;
      return this.batches.filter(val => {
        return (val.batchId) ? val.batchId.toLowerCase().indexOf(this.batchFilter.toLowerCase()) >= 0 : false;
      });
    },
    groupedBatches() {
      let groups = {};
      this.filteredBatches.forEach((row) => {
        if (!row['batchId']) {
          if (!groups['_']) {
            groups['_'] = {};
          }
          if (!groups['_'][row.productLineName]) {
            groups['_'][row.productLineName] = {};
          }
          groups['_'][row.productLineName][row.status] = row;
          return;
        }
        if (!groups[row.batchId]) {
          groups[row.batchId] = {};
        }
        if (!groups[row.batchId][row.productLineName]) {
          groups[row.batchId][row.productLineName] = {};
        }
        groups[row.batchId][row.productLineName][row.status] = row;
      });
      return groups;
    },
    batchIds() {
      return Object.keys(this.groupedBatches);
    },
    runningBatchesExist() {
      return this.recalculatingBatchesCompositeIds.length ||
             this.deletingBatchesCompositeIds.length ||
             this.closingBatchesCompositeIds.length;
    }
  },
  methods: {
    initRestService() {
      return new RestService();
    },
    underRecalculation(batchIdKey, productLineKey, statusKey) {
      if (this.recalculatingBatchesCompositeIds.indexOf(batchIdKey + productLineKey + statusKey) >= 0) {
        return this.underRunningJob(batchIdKey, productLineKey, statusKey);
      }
      return { inQueue: false };
    },
    underClosing(batchIdKey, productLineKey, statusKey) {
      if (this.closingBatchesCompositeIds.indexOf(batchIdKey + productLineKey + statusKey) >= 0) {
        return this.underRunningJob(batchIdKey, productLineKey, statusKey);
      }
      return { inQueue: false };
    },
    underDeletion(batchIdKey, productLineKey, statusKey) {
      if (this.deletingBatchesCompositeIds.indexOf(batchIdKey + productLineKey + statusKey) >= 0) {
        return this.underRunningJob(batchIdKey, productLineKey, statusKey);
      }
      return { inQueue: false };
    },
    underRunningJob(batchIdKey, productLineKey, statusKey) {
      let currentJob = this.jobs.find(job =>
        (batchIdKey === job.batchId) && (productLineKey === job.productLine) && (statusKey === job.policyCasesStatus));
      if (currentJob) return { inQueue: true, status: currentJob.status, jobId: currentJob.id };
      return { inQueue: false };
    },
    init() {
      this.getBatches();
    },
    async getBatches() {
      const res = await this.rest.getBatches();
      this.batches = res.data;
    },
    async poll() {
      let changeDetected = this.checkForRecalculateChanges() ||
                           this.checkForCloseChanges() ||
                           this.checkForDeleteChanges();
      if (changeDetected) {
        this.getBatches();
      }
    },
    checkForRecalculateChanges() {
      const initialSize = this.recalculatingBatchesCompositeIds.length;

      this.recalculatingBatchesCompositeIds = this.recalculatingBatchesCompositeIds.filter(recalculatingJob =>
        this.jobs.some(job => (job.batchId + job.productLine + job.policyCasesStatus) === recalculatingJob));

      return initialSize !== this.recalculatingBatchesCompositeIds.length;
    },
    /**
     * if the job for the batch that has been under deletion is not longer among the running or pending jobs ,
     * remove it from the list and refresh the screen at the end
     */
    checkForDeleteChanges() {
      const initialSize = this.deletingBatchesCompositeIds.length;

      const arraysOfDeletingAndDeleted = this.deletingBatchesCompositeIds.reduce((acc, compositeId) => {
        (this.jobs.some(job => (job.batchId + job.productLine + job.policyCasesStatus) === compositeId))
          ? acc.stillDeleting.push(compositeId) : acc.alreadyDeleted.push(compositeId);
        return acc;
      }, {stillDeleting: [], alreadyDeleted: []});

      this.deletingBatchesCompositeIds = arraysOfDeletingAndDeleted.stillDeleting;

      this.selected = this.selected.filter(batch => (
        arraysOfDeletingAndDeleted.alreadyDeleted.indexOf(batch.batchId + batch.productLine + batch.status) < 0));

      // we need to also clear the selection of full batches here, because it is removed from the table
      let updatedFullBatchIds = [];
      this.selectedFullBatchIds.forEach(fullBatchId => {
        if (this.jobs.some(job => job.batchId === fullBatchId)) {
          updatedFullBatchIds.push(fullBatchId);
        }
      });
      this.selectedFullBatchIds = updatedFullBatchIds;

      return initialSize !== this.deletingBatchesCompositeIds.length;
    },
    /**
     * if the job for the batch that has been under closing is not longer among the running or pending jobs ,
     * remove it from the list and refresh the screen at the end
     */
    checkForCloseChanges() {
      const initialSize = this.closingBatchesCompositeIds.length;

      const arraysOfClosingAndClosed = this.closingBatchesCompositeIds.reduce((acc, compositeId) => {
        (this.jobs.some(job => (job.batchId + job.productLine + job.policyCasesStatus) === compositeId))
          ? acc.stillClosing.push(compositeId) : acc.alreadyClosed.push(compositeId);
        return acc;
      }, {stillClosing: [], alreadyClosed: []});

      this.closingBatchesCompositeIds = arraysOfClosingAndClosed.stillClosing;

      this.selected = this.selected.filter(batch => (
        arraysOfClosingAndClosed.alreadyClosed.indexOf(batch.batchId + batch.productLine + batch.status) < 0));

      return initialSize !== this.closingBatchesCompositeIds.length;
    },
    getSelectedBatchesForRecalc() {
      let selectedWithCorrectStatuses = this.selected.filter(batch => !this.rejectedStatusesRecalc.includes(batch.status));
      if (selectedWithCorrectStatuses.length > 0) {
        selectedWithCorrectStatuses.map(batch =>
          this.recalculatingBatchesCompositeIds.push(batch.batchId + batch.productLine + batch.status));
        return selectedWithCorrectStatuses;
      } else {
        return [];
      }
    },
    getSelectedBatchesForClose() {
      let selectedWithCorrectStatuses = this.selected.filter(batch => !this.rejectedStatusesClose.includes(batch.status));
      if (selectedWithCorrectStatuses.length > 0) {
        selectedWithCorrectStatuses.map(batch =>
          this.closingBatchesCompositeIds.push(batch.batchId + batch.productLine + batch.status));
        return selectedWithCorrectStatuses;
      } else {
        return [];
      }
    },
    getSelectedBatchesForDelete() {
      this.selected.forEach(selectedBatch =>
        this.deletingBatchesCompositeIds.push(selectedBatch.batchId + selectedBatch.productLine + selectedBatch.status));
      return this.selected;
    },
    toggleSelectBatch(e, batchId) {
      if (e.target.checked) {
        this.batches.forEach(batch => {
          if ((batch.batchId === batchId || (batchId === '_' && (batch.batchId === '' || batch.batchId == null))) &&
            !this.selected.includes(batch)) {
            this.selected.push(batch);
          }
        });
      } else {
        this.selected = this.selected.filter(batch => !(batch.batchId === batchId || (batchId === '_' && (batch.batchId === '' || batch.batchId == null))));
      }
    },
    selectAllBatches() {
      this.selected = this.batches;
      this.selectedFullBatchIds = this.batchIds;
    },
    deselectAllBatches() {
      this.selected = [];
      this.selectedFullBatchIds = [];
    },
    disableBatchSelection() {
      this.batchSelectionDisabled = true;
    },
    enableBatchSelection() {
      this.batchSelectionDisabled = false;
    },
    async getProductlines() {
      try {
        let response = await GeneralConfigService.getAllProductLines();
        this.productLines = response;
      } catch (error) {
        this.loadError = 'ERROR_LOADING_INSURERS';
        this.$log.error(error.response.data);
      }
    },
    getDescriptionByName(name) {
      for (let item of this.productLines) {
        if (item.name === name) {
          return item.description;
        }
      }
      return '-';
    }
  },
  watch: {
    selected(newVal) {
      if (newVal.length === 0) {
        this.$emit('update-allselected', false, false);
      } else if (newVal.length === this.batches.length) {
        this.$emit('update-allselected', true, false);
      } else {
        this.$emit('update-allselected', true, true);
      }
    }
  }
}
</script>



// WEBPACK FOOTER //
// src/components/batch/PolicycaseBatchManagement.vue